import { LayoutGrid, List } from "lucide-react";
import type { ComponentType } from "react";
import type { ViewMode } from "@/hooks/use-view-mode";
import { cn } from "@/lib/utils";

interface ViewToggleProps {
  value: ViewMode;
  onChange: (next: ViewMode) => void;
}

/**
 * Segmented List/Card control for directory toolbars. Sits in the toolbar's
 * `actions` slot; pair it with `useViewMode` for persistence.
 */
export function ViewToggle({ value, onChange }: ViewToggleProps) {
  return (
    <div className="inline-flex items-center rounded-lg border p-0.5">
      <ToggleButton
        active={value === "list"}
        label="List view"
        icon={List}
        onClick={() => onChange("list")}
      />
      <ToggleButton
        active={value === "card"}
        label="Card view"
        icon={LayoutGrid}
        onClick={() => onChange("card")}
      />
    </div>
  );
}

interface ToggleButtonProps {
  active: boolean;
  label: string;
  icon: ComponentType<{ className?: string }>;
  onClick: () => void;
}

function ToggleButton({ active, label, icon: Icon, onClick }: ToggleButtonProps) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        "inline-flex items-center justify-center rounded-md p-1.5 transition-colors",
        active ? "bg-muted text-foreground" : "text-muted-foreground hover:text-foreground",
      )}
    >
      <Icon className="h-4 w-4" />
    </button>
  );
}
